import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { Laptop, Smartphone, Monitor, Eye, EyeOff, Lock, Loader2 } from 'lucide-react';

type UserSession = {
  id: string;
  user_id: string;
  session_token: string;
  user_agent: string | null;
  ip_address: string | null;
  city: string | null;
  last_active: string | null;
  created_at: string | null;
};

export function SecurityTab() {
  const [sessions, setSessions] = useState<UserSession[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  // Password change state
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSavingPassword, setIsSavingPassword] = useState(false);

  useEffect(() => {
    loadSessions();
  }, []);

  const loadSessions = async () => {
    try {
      const { data, error } = await supabase
        .from('user_sessions')
        .select('*')
        .order('last_active', { ascending: false });

      if (error) throw error;
      setSessions(data || []);
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOutAll = async () => {
    try {
      const { error } = await supabase.auth.signOut({ scope: 'global' });
      if (error) throw error;
      toast({
        title: 'Sesiones cerradas',
        description: 'Se han cerrado todas las sesiones correctamente',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'No se pudieron cerrar las sesiones',
        variant: 'destructive',
      });
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      toast({
        title: 'Contraseña demasiado corta',
        description: 'La contraseña debe tener al menos 8 caracteres.',
        variant: 'destructive',
      });
      return;
    }
    if (newPassword !== confirmPassword) {
      toast({
        title: 'Las contraseñas no coinciden',
        description: 'Revisa que ambos campos sean iguales.',
        variant: 'destructive',
      });
      return;
    }

    setIsSavingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast({
        title: 'Contraseña actualizada ✓',
        description: 'Tu contraseña se ha cambiado correctamente.',
      });
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      toast({
        title: 'Error al cambiar la contraseña',
        description: error?.message || 'Inténtalo de nuevo más tarde.',
        variant: 'destructive',
      });
    } finally {
      setIsSavingPassword(false);
    }
  };

  const getDeviceIcon = (userAgent: string | null) => {
    if (!userAgent) return <Monitor className="h-5 w-5" />;
    const ua = userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return <Smartphone className="h-5 w-5" />;
    }
    if (ua.includes('tablet') || ua.includes('ipad')) {
      return <Laptop className="h-5 w-5" />;
    }
    return <Monitor className="h-5 w-5" />;
  };

  const formatUserAgent = (userAgent: string | null) => {
    if (!userAgent) return 'Dispositivo desconocido';
    const ua = userAgent;
    let browser = 'Navegador desconocido';
    let os = 'SO desconocido';
    if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';
    return `${browser} en ${os}`;
  };

  return (
    <div className="space-y-6">

      {/* ─── CAMBIAR CONTRASEÑA ─── */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Lock className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold">Cambiar contraseña</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Elige una contraseña segura de al menos 8 caracteres.
        </p>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="new-password">Nueva contraseña</Label>
            <div className="relative">
              <Input
                id="new-password"
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setShowNew((v) => !v)}
                tabIndex={-1}
              >
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="confirm-password">Confirmar nueva contraseña</Label>
            <div className="relative">
              <Input
                id="confirm-password"
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repite la contraseña"
                className="pr-10"
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                onClick={() => setShowConfirm((v) => !v)}
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {/* Strength hint */}
          {newPassword.length > 0 && (
            <p className={`text-xs ${newPassword.length >= 8 ? 'text-green-600' : 'text-amber-500'}`}>
              {newPassword.length >= 8 ? '✓ Longitud correcta' : `${8 - newPassword.length} caracteres más necesarios`}
            </p>
          )}
          {confirmPassword.length > 0 && newPassword !== confirmPassword && (
            <p className="text-xs text-red-500">Las contraseñas no coinciden</p>
          )}

          <Button
            onClick={handleChangePassword}
            disabled={isSavingPassword || !newPassword || !confirmPassword}
            className="w-full bg-gradient-to-r from-primary to-secondary text-white hover:opacity-90"
          >
            {isSavingPassword ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Guardando...
              </>
            ) : (
              'Cambiar contraseña'
            )}
          </Button>
        </div>
      </div>

      <Separator />

      {/* ─── SESIONES ACTIVAS ─── */}
      <div>
        <h3 className="text-lg font-semibold mb-2">Sesiones activas</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Gestiona los dispositivos donde has iniciado sesión
        </p>

        {loading ? (
          <div className="text-center py-8 text-muted-foreground">
            Cargando sesiones...
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No hay sesiones activas
          </div>
        ) : (
          <div className="space-y-3">
            {sessions.map((session) => (
              <div key={session.id} className="flex items-start gap-3 p-4 rounded-lg border bg-card">
                <div className="text-muted-foreground mt-0.5">
                  {getDeviceIcon(session.user_agent)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">
                    {formatUserAgent(session.user_agent)}
                  </p>
                  {session.city && (
                    <p className="text-xs text-muted-foreground">
                      {session.city}
                    </p>
                  )}
                  {session.last_active && (
                    <p className="text-xs text-muted-foreground">
                      Última actividad: {new Date(session.last_active).toLocaleString('es-ES')}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        <Button
          onClick={handleSignOutAll}
          variant="outline"
          className="w-full mt-4 bg-gradient-to-r from-primary to-secondary text-white hover:opacity-90 border-0"
        >
          Cerrar sesión en todos los dispositivos
        </Button>
      </div>

      <Separator />

      {/* ─── 2FA ─── */}
      <div>
        <h3 className="text-lg font-semibold mb-2">Autenticación 2FA</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Añade una capa extra de seguridad a tu cuenta
        </p>
        <Button disabled variant="outline" className="w-full">
          Próximamente disponible
        </Button>
      </div>
    </div>
  );
}
